import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "antd";
import Segmented from "../../components/segmented/Segmented.jsx";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import Pagination from "../../components/global/pagination/Pagination.jsx";
import UserAvatar from "../../components/global/avtar/UserAvatar.jsx";
import Eye from "../../assets/Eye.jsx";
import { GetCorporateDocumentsReviewListApi } from "../../api/request/corporate.js";
import { handleApiError } from "../../utils/handleApiError.js";

const getStatusTagClass = (statusText = "") => {
  return statusText === "Approved" ? "success_tag" : "fail_tag";
};

export default function CorporateReview() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState("Pending");
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getCorporateReviewList = async () => {
    try {
      setIsLoading(true);
      const params = {
        FilterText: status,
        SearchText: searchText?.trim(),
        PageNumber: page,
        PageSize: pageSize,
        ApplyPagination: true,
      };
      const { data } = await GetCorporateDocumentsReviewListApi(
        new URLSearchParams(params).toString()
      );
      if (data?.success) {
        setData(data?.data || []);
        setTotalRecord(data?.totalRecords || 0);
      } else {
        setData([]);
        setTotalRecord(0);
      }
    } catch (error) {
      handleApiError(error);
      setData([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCorporateReviewList();
  }, [status, page, pageSize, searchText]);

  useEffect(() => {
    setPage(1);
  }, [status, searchText]);

  useEffect(() => {
    setSearchParams(`filter=${status}`);
  }, [status]);

  useEffect(() => {
    const filter = searchParams.get("filter")?.trim();
    if (filter && ["Pending", "Approved", "Rejected"].includes(filter)) {
      setStatus(filter);
    }
  }, []);

  const columns = [
    {
      title: "Client",
      dataIndex: "fullName",
      key: "fullName",
      render: (_, record) => (
        <UserAvatar data={{ name: record?.fullName, email: record?.email }} />
      ),
    },
    {
      title: "Status",
      dataIndex: "statusText",
      key: "statusText",
      render: (_, record) => (
        <span
          className={`px-3 py-1 rounded-full uppercase text-xs text-center ${getStatusTagClass(
            record?.statusText
          )}`}
        >
          {record?.statusText || "-"}
        </span>
      ),
    },
    {
      title: "Progress",
      dataIndex: "completedPercentage",
      key: "completedPercentage",
      render: (_, record) => `${record?.completedPercentage ?? 0}%`,
    },
    ...(status !== "Pending"
      ? [
          {
            title: "Reviewed Date",
            dataIndex: "reviewedDate",
            key: "reviewedDate",
            render: (_, record) =>
              record?.reviewedDate
                ? new Date(record?.reviewedDate).toLocaleString()
                : "-",
          },
        ]
      : []),
    ...(status === "Rejected"
      ? [
          {
            title: "Reason",
            dataIndex: "rejectionReason",
            key: "rejectionReason",
            render: (_, record) => record?.rejectionReason || "-",
          },
        ]
      : []),
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <button
          type="button"
          onClick={() =>
            navigate(`/corporate_review/${record?.userId}?filter=${status}`)
          }
        >
          <Eye />
        </button>
      ),
    },
  ];

  return (
    <div className="max-w-full">
      <div className="flex justify-between flex-wrap gap-5 items-center">
        <Segmented
          options={["Pending", "Approved", "Rejected"]}
          value={status}
          setValue={setStatus}
        />
        <Input
          size="large"
          style={{ width: 260 }}
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => setSearchText(search)}
          prefix={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                clipRule="evenodd"
              />
            </svg>
          }
          suffix={
            search && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => {
                  setSearch("");
                  setSearchText("");
                }}
                viewBox="0 0 24 24"
                fill="#bebebe"
                className="size-5 cursor-pointer duration-300 hover:fill-[#969595]"
              >
                <path
                  fillRule="evenodd"
                  d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                  clipRule="evenodd"
                />
              </svg>
            )
          }
        />
      </div>

      <div className="my-6">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={columns}
            data={data}
            loading={isLoading}
            rowKey="corporateAccountId"
            page={page}
            pageSize={pageSize}
          />
        </div>
        <Pagination
          total={totalRecord}
          pageSize={pageSize}
          setPageSize={setPageSize}
          page={page}
          setPage={setPage}
        />
      </div>
    </div>
  );
}
